import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { useBusinessPlanAccess } from "@/hooks/use-business-plan-access"
import { usePayBusinessSubscriptionMutation } from "@/queries/business/business-plan"
import { toast } from "sonner"
import { formatAxiosErrorMessage } from "@/lib/utils"
import { openPaystackCheckout } from "@/lib/paystack-checkout"
import {
  invalidateSubscriptionQueries,
  pollSubscriptionPaymentConfirmation,
} from "@/lib/paystack-payment-confirmation"
import { useQueryClient } from "@tanstack/react-query"
import { CloseCircle, StarsMinimalistic } from "@solar-icons/react"

function dismissKey(businessId: string) {
  return `premium-payment-banner-dismissed-${businessId}`
}

export function PremiumPaymentBanner() {
  const { businessId, needsPayment } = useBusinessPlanAccess()
  const { mutateAsync: paySubscription, isPending } = usePayBusinessSubscriptionMutation()
  const queryClient = useQueryClient()
  const pollStopRef = useRef<(() => void) | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!businessId) return
    setDismissed(sessionStorage.getItem(dismissKey(businessId)) === "true")
  }, [businessId])

  useEffect(() => {
    return () => {
      pollStopRef.current?.()
      pollStopRef.current = null
    }
  }, [])

  const handlePaymentConfirmed = () => {
    if (!businessId) return
    pollStopRef.current?.()
    pollStopRef.current = null
    toast.success("Premium subscription activated")
    invalidateSubscriptionQueries(queryClient, businessId)
  }

  const startConfirmation = (references: Array<string | undefined>) => {
    pollStopRef.current?.()
    pollStopRef.current = pollSubscriptionPaymentConfirmation({
      references,
      onConfirmed: handlePaymentConfirmed,
      onPending: () =>
        toast.message("Payment is processing. Refresh shortly if features stay locked."),
    })
  }

  const handlePay = async () => {
    if (!businessId) return
    try {
      const response = await paySubscription(businessId)
      const payload = response.data
      if (payload?.activated) {
        handlePaymentConfirmed()
        return
      }
      const internalReference = payload?.reference
      const paystackReference = payload?.paystackReference
      const confirmationRefs = [internalReference, paystackReference]
      const started = openPaystackCheckout(
        {
          accessCode: payload?.accessCode,
          url: payload?.url,
          reference: internalReference,
          paystackReference,
        },
        {
          onStarted: () => startConfirmation(confirmationRefs),
          onSuccess: (completedReference) =>
            startConfirmation([completedReference, ...confirmationRefs]),
          onError: (message) => toast.error(message),
        }
      )
      if (!started) {
        toast.error("Could not start payment")
      }
    } catch (error: unknown) {
      toast.error(formatAxiosErrorMessage(error, "Failed to start payment"))
    }
  }

  const handleDismiss = () => {
    if (!businessId) return
    sessionStorage.setItem(dismissKey(businessId), "true")
    setDismissed(true)
  }

  if (!needsPayment || dismissed) {
    return null
  }

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/40">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <StarsMinimalistic className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0">
            <p className="font-medium text-amber-950 dark:text-amber-100">
              Complete your Premium subscription
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={handlePay} disabled={isPending} className="bg-amber-700 text-white hover:bg-amber-800 dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-amber-950">
            {isPending ? "Starting…" : "Complete payment"}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-8 text-amber-800 hover:text-amber-950 dark:text-amber-200"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
          >
            <CloseCircle className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
